import { ComponentProps, ReactNode, useMemo } from 'react';
import { Box, Table as ChakraTable, TableProps } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { FlexRow } from 'components/layout/FlexRow';
import withScrollableContainer from 'components/layout/Table/withScrollableContainer';

const cardStyle = {
  width: '100%',
  background: 'brand.white',
  border: '1px solid',
  borderColor: Colors.ArmyGreen300,
  borderRadius: '16px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const headerBarStyle = {
  flexShrink: 0,
  padding: 'padding_12',
  borderBottom: '1px solid',
  borderColor: 'brand.dark-green-200',
  backgroundColor: Colors.ArmyGreen100,
};

const headerContentStyle = {
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const footerBarStyle = {
  flexShrink: 0,
  padding: 'padding_8',
  borderTop: '1px solid',
  borderColor: Colors.ArmyGreen200,
};

// Lets the middle scroll region shrink below its content size inside the flex
// card so it - not the whole card - is what scrolls horizontally/vertically.
const bodyContainerStyle = {
  flex: '1 1 auto',
  minHeight: 0,
};

const tableStyles = {
  boxSizing: 'border-box',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  width: '100%',
  background: 'brand.white',
  border: '1px solid',
  borderColor: 'brand.dark-green-200',
  borderRadius: '6px',
  whiteSpace: 'unset',
  'th, td': {
    padding: 'padding_8',
    position: 'relative',
  },
  'tbody tr:last-child': {
    'td:after': {
      display: 'none',
    },
  },
  'th:after, td:after': {
    content: '""',
    background: 'brand.dark-green-200',
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '1px',
  },
  'th:first-of-type:after, td:first-of-type:after': {
    left: 'padding_8',
  },
  'th:last-of-type:after, td:last-of-type:after': {
    right: 'padding_8',
  },
};

type CoreProps = TableProps & {
  sx?: TableProps['sx'];
  children?: ReactNode;
};

const TableCore = ({ sx = {}, children, ...props }: CoreProps) => {
  const styles = useMemo(() => {
    return { ...tableStyles, ...sx };
  }, [sx]);

  return (
    <ChakraTable variant="unstyled" sx={styles} {...props}>
      {children}
    </ChakraTable>
  );
};

const ScrollableTable = withScrollableContainer(TableCore);

type Props = ComponentProps<typeof ScrollableTable> & {
  // Content shown in a bar above the table, e.g. a result count.
  header?: ReactNode;
  // Content shown at the right end of the header bar, e.g. a column selector
  // or other per-table settings controls.
  settings?: ReactNode;
  // Content shown in a bar below the table, e.g. pagination controls.
  footer?: ReactNode;
  cardSx?: TableProps['sx'];
};

const Table = ({
  header,
  settings,
  footer,
  cardSx = {},
  sx = {},
  containerSx = {},
  ...props
}: Props) => {
  if (!header && !settings && !footer) {
    return <ScrollableTable sx={sx} containerSx={containerSx} {...props} />;
  }

  // Plain text/number headers (the common case - a result count) get the
  // table's uniform heading style for free. Anything more custom (e.g. a
  // caller-composed FlexRow) is trusted to bring its own styling.
  const headerContent =
    typeof header === 'string' || typeof header === 'number' ? (
      <Text.LargeBold>{header}</Text.LargeBold>
    ) : (
      header
    );

  return (
    <Box sx={{ ...cardStyle, ...cardSx }}>
      {(header || settings) && (
        <Box sx={headerBarStyle}>
          <FlexRow sx={headerContentStyle}>
            <FlexRow.Left>{headerContent}</FlexRow.Left>
            {settings && <FlexRow.Right>{settings}</FlexRow.Right>}
          </FlexRow>
        </Box>
      )}
      <ScrollableTable
        sx={{ ...sx, border: 'none', borderRadius: 0 }}
        containerSx={{ ...bodyContainerStyle, ...containerSx }}
        {...props}
      />
      {footer && <Box sx={footerBarStyle}>{footer}</Box>}
    </Box>
  );
};

export default Table;
